import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Terminal } from "@xterm/xterm";
import { FitAddon } from "@xterm/addon-fit";
import "@xterm/xterm/css/xterm.css";
import api from "../api/client";
import { usePublishScreenData } from "../utils/aiScreenContext";
import { NetworkDevice, Customer, SwitchDetail, SwitchPort } from "../types";
import { formatDateTime, formatRelative } from "../utils/datetime";
import { useAuth } from "../context/AuthContext";
import { useCustomer } from "../context/CustomerContext";
import { useSort, sortRows, SortableTh } from "../components/SortableTh";
import SnmpDiagnosisPanel, { SnmpDiagnosisBrief } from "../components/SnmpDiagnosisPanel";

const KIND_OPTIONS = [
  "switch",
  "firewall",
  "router",
  "printer",
  "ups",
  "access_point",
  "nas",
  "other",
] as const;

// Semantic health order (online first) for the Status column sort.
// `unmonitored` sits between online and offline on purpose: the device is
// UP - it answered and refused our credentials - so it is healthier than one
// that is down, but it is not being read, so it is not as good as online.
// `never_answered` sits BELOW offline: the sort is worst-first for someone
// triaging the screen, and a real outage needs attention before a monitor that
// was never finished. It is still not an outage - see the badge tooltip.
const NET_STATUS_RANK: Record<string, number> = {
  online: 0,
  unmonitored: 1,
  offline: 2,
  never_answered: 3,
  error: 4,
};

export default function Network() {
  const { user } = useAuth();
  const canManage = user?.role === "admin" || user?.role === "technician";
  const canDelete = user?.role === "admin";

  const [devices, setDevices] = useState<NetworkDevice[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [editing, setEditing] = useState<Partial<NetworkDevice> | null>(null);
  const [viewing, setViewing] = useState<NetworkDevice | null>(null);
  const [pollMsg, setPollMsg] = useState("");
  const [search, setSearch] = useState("");

  const { currentCustomerId } = useCustomer();
  const { sort, toggle } = useSort("name", "asc");

  // Publish the visible rows so the global AI Assist can answer
  // "what's on my screen". Projected explicitly: this payload goes
  // into a model prompt.
  usePublishScreenData(
    "Network devices",
    useMemo(
      () =>
        devices.map((d) => ({
          name: d.name,
          ip: d.ip_address,
          kind: d.kind,
          customer: d.customer_name ?? null,
          monitor_type: d.monitor_type ?? null,
          enabled: d.enabled,
          last_status: (d as any).last_status ?? null,
          last_polled_at: (d as any).last_polled_at ?? null,
          sys_name: (d as any).sys_name ?? null,
          // Deliberately NOT snmp_community, nor any snmp_v3_* field. This
          // row type holds live credentials in plain text and none of them
          // belongs in a model prompt.
        })),
      [devices],
    ),
    search ? `search: ${search}` : undefined,
  );

  const valueOf = (d: NetworkDevice, key: string): string | number => {
    switch (key) {
      case "kind":
        return (d.kind || "").toLowerCase();
      case "ip_address": {
        const parts = (d.ip_address || "").split(".");
        return parts.length === 4
          ? parts.reduce((acc, p) => acc * 256 + (parseInt(p, 10) || 0), 0)
          : 0;
      }
      case "customer_name":
        return (d.customer_name || "").toLowerCase();
      case "last_status":
        return NET_STATUS_RANK[d.last_status] ?? 99;
      case "last_polled_at":
        return d.last_polled_at ? Date.parse(d.last_polled_at) : 0;
      default:
        return (d.name || "").toLowerCase();
    }
  };

  // Scope the network-device list to the globally-selected customer (null = all).
  const load = useCallback(() => {
    const cfg =
      currentCustomerId != null ? { params: { customer_id: currentCustomerId } } : undefined;
    return api.get("/api/v1/network-devices", cfg).then((r) => setDevices(r.data));
  }, [currentCustomerId]);
  useEffect(() => {
    load();
  }, [load]);
  useEffect(() => {
    api.get("/api/v1/customers").then((r) => setCustomers(r.data));
  }, []);

  const filtered = useMemo(() => {
    if (!search) return devices;
    const q = search.toLowerCase();
    return devices.filter(
      (d) =>
        d.name.toLowerCase().includes(q) ||
        d.ip_address.toLowerCase().includes(q) ||
        (d.customer_name || "").toLowerCase().includes(q) ||
        (d.sys_name || "").toLowerCase().includes(q),
    );
  }, [devices, search]);

  const save = async () => {
    if (!editing) return;
    try {
      if (editing.id) {
        await api.put(`/api/v1/network-devices/${editing.id}`, {
          name: editing.name,
          ip_address: editing.ip_address,
          kind: editing.kind,
          monitor_type: editing.monitor_type || "snmp",
          snmp_version: editing.snmp_version,
          snmp_community: editing.snmp_community,
          snmp_port: editing.snmp_port,
          enabled: editing.enabled,
          ssh_username: editing.ssh_username,
          ssh_port: editing.ssh_port || 22,
          ssh_password: editing.ssh_password || undefined,
          snmp_v3_user: editing.snmp_v3_user,
          snmp_v3_auth_protocol: editing.snmp_v3_auth_protocol,
          snmp_v3_priv_protocol: editing.snmp_v3_priv_protocol,
          snmp_v3_auth_password: editing.snmp_v3_auth_password || undefined,
          snmp_v3_priv_password: editing.snmp_v3_priv_password || undefined,
        });
      } else {
        await api.post("/api/v1/network-devices", {
          customer_id: editing.customer_id,
          name: editing.name || "",
          ip_address: editing.ip_address || "",
          kind: editing.kind || "other",
          monitor_type: editing.monitor_type || "snmp",
          snmp_version: editing.snmp_version || "v2c",
          snmp_community: editing.snmp_community || "public",
          snmp_port: editing.snmp_port || 161,
          enabled: editing.enabled !== false,
          ssh_username: editing.ssh_username,
          ssh_port: editing.ssh_port || 22,
          ssh_password: editing.ssh_password || undefined,
          snmp_v3_user: editing.snmp_v3_user,
          snmp_v3_auth_protocol: editing.snmp_v3_auth_protocol,
          snmp_v3_priv_protocol: editing.snmp_v3_priv_protocol,
          snmp_v3_auth_password: editing.snmp_v3_auth_password || undefined,
          snmp_v3_priv_password: editing.snmp_v3_priv_password || undefined,
        });
      }
      setEditing(null);
      load();
    } catch (err: any) {
      alert(err.response?.data?.detail || "Save failed");
    }
  };

  const deleteDevice = async (d: NetworkDevice) => {
    if (!window.confirm(`Delete "${d.name}" (${d.ip_address})? This cannot be undone.`)) return;
    await api.delete(`/api/v1/network-devices/${d.id}`);
    load();
  };

  const pollNow = async (d: NetworkDevice) => {
    setPollMsg(`Polling ${d.name}…`);
    try {
      const r = await api.post(`/api/v1/network-devices/${d.id}/poll-now`);
      // Update in place. For off-network (agent-relayed) devices the poll runs
      // on the on-site collector and the fresh status arrives a few seconds
      // later — refresh the list shortly so it shows up without a manual reload.
      setDevices((prev) => prev.map((x) => (x.id === d.id ? r.data : x)));
      if (viewing?.id === d.id) setViewing(r.data);
      if (r.data.poll_via_agent) {
        setPollMsg(
          `Poll requested for ${d.name} — the on-site agent will report status in a few seconds…`,
        );
        setTimeout(() => load(), 6000);
      } else {
        setPollMsg(
          `${d.name}: ${r.data.last_status}` + (r.data.last_error ? ` — ${r.data.last_error}` : ""),
        );
      }
    } catch (err: any) {
      setPollMsg(`Poll failed for ${d.name}: ${err.response?.data?.detail || err.message}`);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-1">Network devices</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            SNMP-polled switches, firewalls, printers, UPSes. Polled every 5 minutes; critical alert
            fires after 2 consecutive missed polls.
          </p>
        </div>
        {canManage && (
          <button
            onClick={() =>
              setEditing({
                customer_id: customers[0]?.id,
                name: "",
                ip_address: "",
                kind: "other",
                snmp_version: "v2c",
                snmp_community: "public",
                snmp_port: 161,
                enabled: true,
              })
            }
            className="text-sm bg-blue-600 text-white px-4 py-1.5 rounded-lg hover:bg-blue-700"
          >
            + Add device
          </button>
        )}
      </div>

      {pollMsg && (
        <div className="mb-3 flex items-center justify-between gap-3 rounded-lg border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20 px-3 py-2 text-sm text-blue-800 dark:text-blue-200">
          <span>{pollMsg}</span>
          <button
            onClick={() => setPollMsg("")}
            className="text-blue-500 hover:text-blue-700 text-xs"
          >
            Dismiss
          </button>
        </div>
      )}

      <div className="mb-3">
        <input
          type="text"
          placeholder="Search by name, IP, customer, or sysName…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-3 py-2 text-sm w-96"
        />
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm overflow-x-auto">
        {filtered.length === 0 ? (
          <p className="text-sm text-gray-400 text-center py-10">
            {devices.length === 0
              ? 'No network devices yet. Click "+ Add device" to monitor one.'
              : "No devices match your search."}
          </p>
        ) : (
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-900/50 border-b dark:border-gray-700 text-xs text-gray-500 dark:text-gray-400 uppercase">
              <tr>
                <SortableTh label="Name" sortKey="name" sort={sort} onSort={toggle} />
                <SortableTh label="Kind" sortKey="kind" sort={sort} onSort={toggle} hide="hidden md:table-cell" />
                <SortableTh label="IP" sortKey="ip_address" sort={sort} onSort={toggle} hide="hidden sm:table-cell" />
                <SortableTh label="Customer" sortKey="customer_name" sort={sort} onSort={toggle} hide="hidden lg:table-cell" />
                <SortableTh label="Status" sortKey="last_status" sort={sort} onSort={toggle} />
                <SortableTh label="Last polled" sortKey="last_polled_at" sort={sort} onSort={toggle} hide="hidden lg:table-cell" />
                {canManage && <th className="px-4 py-2 text-right">Actions</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {(
                [
                  {
                    key: "net",
                    label: "Network Devices",
                    glyph: "🌐",
                    items: filtered.filter((d) => d.kind !== "printer"),
                  },
                  {
                    key: "printer",
                    label: "Printers",
                    glyph: "🖨️",
                    items: filtered.filter((d) => d.kind === "printer"),
                  },
                ] as const
              ).map((g) =>
                g.items.length === 0 ? null : (
                  <React.Fragment key={g.key}>
                    <tr className="bg-gray-50 dark:bg-gray-900 border-y border-gray-200 dark:border-gray-700">
                      <td
                        colSpan={canManage ? 7 : 6}
                        className="px-4 py-2 text-xs font-semibold text-gray-600 dark:text-gray-300"
                      >
                        {g.glyph} {g.label}{" "}
                        <span className="font-normal text-gray-400">({g.items.length})</span>
                      </td>
                    </tr>
                    {sortRows(g.items, sort, valueOf).map((d) => (
                      <tr
                        key={d.id}
                        className="hover:bg-gray-50 dark:hover:bg-gray-700/40 cursor-pointer"
                        onClick={() => setViewing(d)}
                      >
                        <td className="px-4 py-2.5 font-medium text-gray-900 dark:text-gray-100">{d.name}</td>
                        <td className="px-4 py-2.5 text-xs hidden md:table-cell">{kindBadge(d.kind)}</td>
                        <td className="px-4 py-2.5 font-mono text-xs text-gray-600 dark:text-gray-400 hidden sm:table-cell">
                          {d.ip_address}
                        </td>
                        <td className="px-4 py-2.5 text-xs text-gray-600 dark:text-gray-400 hidden lg:table-cell">
                          {d.customer_name || "—"}
                        </td>
                        <td className="px-4 py-2.5">
                          <div className="relative inline-flex group">
                            {statusBadge(d.last_status)}
                            <div
                              className="pointer-events-none absolute left-0 top-full mt-1.5 z-30 hidden group-hover:block
                          w-72 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900
                          shadow-xl p-3 text-xs text-gray-700 dark:text-gray-200 text-left normal-case"
                            >
                              <div className="mb-1">{statusBadge(d.last_status)}</div>
                              <div>
                                Monitoring: {d.monitor_type === "icmp" ? "ICMP ping" : "SNMP"}
                              </div>
                              <div>
                                Last polled:{" "}
                                {d.last_polled_at
                                  ? formatDateTime(d.last_polled_at)
                                  : "never"}
                              </div>
                              {d.last_error ? (
                                <div className="mt-1">
                                  <div className="text-gray-500 dark:text-gray-400">
                                    Last error:
                                  </div>
                                  <div className="font-mono text-[11px] text-red-600 dark:text-red-400 whitespace-pre-wrap break-words max-h-32 overflow-hidden">
                                    {d.last_error}
                                  </div>
                                  {/* Beside the raw protocol string, never instead
                                      of it — an unrecognised failure yields no
                                      diagnosis, and that is when the raw text
                                      matters most. */}
                                  {d.error_diagnosis ? (
                                    <SnmpDiagnosisBrief d={d.error_diagnosis} />
                                  ) : null}
                                </div>
                              ) : null}
                              <div className="mt-1.5 pt-1.5 border-t border-gray-100 dark:border-gray-800 text-[10px] text-gray-400">
                                Click the row for full details
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-2.5 text-xs text-gray-500 dark:text-gray-400 hidden lg:table-cell">
                          {d.last_polled_at
                            ? formatRelative(d.last_polled_at)
                            : "never"}
                        </td>
                        {canManage && (
                          <td
                            className="px-4 py-2.5 text-right whitespace-nowrap"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <button
                              onClick={() => pollNow(d)}
                              className="text-xs border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 px-2 py-1 rounded mr-1 hover:bg-gray-100 dark:hover:bg-gray-700/40"
                            >
                              Poll
                            </button>
                            <button
                              onClick={() => setEditing({ ...d })}
                              className="text-xs border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 px-2 py-1 rounded mr-1 hover:bg-gray-100 dark:hover:bg-gray-700/40"
                            >
                              Edit
                            </button>
                            {canDelete && (
                              <button
                                onClick={() => deleteDevice(d)}
                                className="text-xs border border-red-300 dark:border-red-800 text-red-700 dark:text-red-400 px-2 py-1 rounded hover:bg-red-50 dark:hover:bg-red-900/20"
                              >
                                Delete
                              </button>
                            )}
                          </td>
                        )}
                      </tr>
                    ))}
                  </React.Fragment>
                ),
              )}
            </tbody>
          </table>
        )}
      </div>

      {editing && (
        <DeviceModal
          draft={editing}
          customers={customers}
          onChange={setEditing}
          onSave={save}
          onCancel={() => setEditing(null)}
        />
      )}
      {viewing && (
        <DetailModal
          device={viewing}
          onClose={() => setViewing(null)}
          onPoll={() => pollNow(viewing)}
          onEdit={() => {
            setEditing({ ...viewing });
            setViewing(null);
          }}
          canManage={canManage}
        />
      )}
    </div>
  );
}

function kindBadge(kind: string) {
  const colors: Record<string, string> = {
    switch: "bg-purple-50 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300",
    firewall: "bg-red-50 text-red-700 dark:bg-red-900/40 dark:text-red-300",
    router: "bg-blue-50 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300",
    printer: "bg-amber-50 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300",
    ups: "bg-green-50 text-green-700 dark:bg-green-900/40 dark:text-green-300",
    access_point: "bg-indigo-50 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300",
    nas: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300",
    other: "bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300",
  };
  return (
    <span className={`text-xs px-2 py-0.5 rounded-full ${colors[kind] || colors.other}`}>
      {kind}
    </span>
  );
}

// Four states, and only ONE of them is an outage. A device that rejects our
// SNMP credential composed that rejection and sent it, which proves it is
// running - colouring it red states an outage that is not happening.
const NET_STATUS_STYLE: Record<string, { cls: string; label: string; title: string }> = {
  online: {
    cls: "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300",
    label: "online",
    title: "Answering normally.",
  },
  offline: {
    cls: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300",
    label: "offline",
    title: "Nothing answered. This one may really be down.",
  },
  unmonitored: {
    cls: "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300",
    label: "unmonitored",
    title:
      "The device answered and refused our credentials, so it is running — " +
      "we just cannot read it. Not an outage.",
  },
  never_answered: {
    cls: "bg-slate-200 text-slate-700 dark:bg-slate-700/60 dark:text-slate-200",
    label: "never answered",
    title:
      "This device has never once answered since it was added, so there is no " +
      "outage to report — nothing has changed. Check the address, the SNMP " +
      "version and credentials, and whether SNMP is enabled on the device at " +
      "all. Cloud-managed gear (Meraki, UniFi) is usually read through its " +
      "controller rather than by SNMP.",
  },
  error: {
    cls: "bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300",
    label: "not polled",
    title:
      "AeroGauge's own settings for this device are incomplete, so no poll " +
      "was sent. Nothing is known about the device either way.",
  },
};

function statusBadge(status: string) {
  const s = NET_STATUS_STYLE[status] || {
    cls: "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300",
    label: status,
    title: "",
  };
  return (
    <span title={s.title} className={`text-xs px-2 py-0.5 rounded-full ${s.cls}`}>
      {s.label}
    </span>
  );
}

function DeviceModal({
  draft,
  customers,
  onChange,
  onSave,
  onCancel,
}: {
  draft: Partial<NetworkDevice>;
  customers: Customer[];
  onChange: (d: Partial<NetworkDevice>) => void;
  onSave: () => void;
  onCancel: () => void;
}) {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 dark:text-gray-100 rounded-xl shadow-xl w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto">
        <h2 className="text-lg font-semibold mb-4">
          {draft.id ? `Edit "${draft.name}"` : "Add network device"}
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
          <div>
            <label className="text-xs font-semibold text-gray-600 dark:text-gray-300 block mb-1">Name</label>
            <input
              type="text"
              value={draft.name || ""}
              onChange={(e) => onChange({ ...draft, name: e.target.value })}
              className="w-full border dark:bg-gray-900 dark:border-gray-600 dark:text-gray-100 rounded-lg px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-600 dark:text-gray-300 block mb-1">IP address</label>
            <input
              type="text"
              value={draft.ip_address || ""}
              onChange={(e) => onChange({ ...draft, ip_address: e.target.value })}
              placeholder="192.168.1.1"
              className="w-full border dark:bg-gray-900 dark:border-gray-600 dark:text-gray-100 rounded-lg px-3 py-2 text-sm font-mono"
            />
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
          <div>
            <label className="text-xs font-semibold text-gray-600 dark:text-gray-300 block mb-1">Kind</label>
            <select
              value={draft.kind || "other"}
              onChange={(e) => onChange({ ...draft, kind: e.target.value })}
              className="w-full border dark:bg-gray-900 dark:border-gray-600 dark:text-gray-100 rounded-lg px-3 py-2 text-sm"
            >
              {KIND_OPTIONS.map((k) => (
                <option key={k} value={k}>
                  {k}
                </option>
              ))}
            </select>
          </div>
          {!draft.id && (
            <div>
              <label className="text-xs font-semibold text-gray-600 dark:text-gray-300 block mb-1">Customer</label>
              <select
                value={draft.customer_id || ""}
                onChange={(e) => onChange({ ...draft, customer_id: Number(e.target.value) })}
                className="w-full border dark:bg-gray-900 dark:border-gray-600 dark:text-gray-100 rounded-lg px-3 py-2 text-sm"
              >
                <option value="">— Select —</option>
                {customers.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
        <label className="flex items-start gap-2 mb-3 p-2.5 rounded-lg border border-teal-200 dark:border-teal-800 bg-teal-50 dark:bg-teal-900/20 cursor-pointer">
          <input
            type="checkbox"
            className="mt-0.5"
            checked={(draft.monitor_type || "snmp") === "icmp"}
            onChange={(e) =>
              onChange({ ...draft, monitor_type: e.target.checked ? "icmp" : "snmp" })
            }
          />
          <span>
            <span className="text-sm font-medium text-gray-800 dark:text-gray-100">
              Monitor by ICMP (ping) only
            </span>
            <span className="block text-xs text-gray-500 dark:text-gray-400">
              For devices that aren&apos;t SNMP-capable — tracks up/down and alerts on down. SNMP
              settings below are ignored.
            </span>
          </span>
        </label>
        {(() => {
          const icmpOnly = (draft.monitor_type || "snmp") === "icmp";
          return (
            <fieldset
              disabled={icmpOnly}
              className={icmpOnly ? "opacity-50 pointer-events-none" : ""}
            >
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
                <div>
                  <label className="text-xs font-semibold text-gray-600 dark:text-gray-300 block mb-1">
                    SNMP version
                  </label>
                  <select
                    value={draft.snmp_version || "v2c"}
                    onChange={(e) => onChange({ ...draft, snmp_version: e.target.value })}
                    className="w-full border dark:bg-gray-900 dark:border-gray-600 dark:text-gray-100 rounded-lg px-3 py-2 text-sm disabled:bg-gray-100 disabled:text-gray-400"
                  >
                    <option value="v2c">v2c</option>
                    <option value="v1">v1</option>
                    <option value="v3">v3</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-600 dark:text-gray-300 block mb-1">
                    Community
                  </label>
                  <input
                    type="text"
                    value={draft.snmp_community || ""}
                    onChange={(e) => onChange({ ...draft, snmp_community: e.target.value })}
                    placeholder="public"
                    disabled={(draft.snmp_version || "v2c") === "v3"}
                    title={
                      (draft.snmp_version || "v2c") === "v3"
                        ? "Community is not used for SNMP v3 — set the USM credentials below."
                        : undefined
                    }
                    className="w-full border dark:bg-gray-900 dark:border-gray-600 dark:text-gray-100 rounded-lg px-3 py-2 text-sm font-mono disabled:bg-gray-100 disabled:text-gray-400"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-600 dark:text-gray-300 block mb-1">Port</label>
                  <input
                    type="number"
                    value={draft.snmp_port || 161}
                    onChange={(e) => onChange({ ...draft, snmp_port: Number(e.target.value) })}
                    className="w-full border dark:bg-gray-900 dark:border-gray-600 dark:text-gray-100 rounded-lg px-3 py-2 text-sm disabled:bg-gray-100 disabled:text-gray-400"
                  />
                </div>
              </div>
              {(draft.snmp_version || "v2c") === "v3" && (
                <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-3 mb-3 space-y-3">
                  <div className="text-xs font-semibold text-gray-600 dark:text-gray-300">
                    SNMP v3 (USM) credentials
                    <span className="font-normal text-gray-400">
                      {" "}
                      — auth + privacy passphrases are encrypted at rest and never shown again.
                    </span>
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-gray-600 dark:text-gray-300 block mb-1">
                      Username
                    </label>
                    <input
                      type="text"
                      value={draft.snmp_v3_user || ""}
                      onChange={(e) => onChange({ ...draft, snmp_v3_user: e.target.value })}
                      placeholder="snmpuser"
                      className="w-full border dark:bg-gray-900 dark:border-gray-600 dark:text-gray-100 rounded-lg px-3 py-2 text-sm font-mono"
                    />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs font-semibold text-gray-600 dark:text-gray-300 block mb-1">
                        Auth protocol
                      </label>
                      <select
                        value={draft.snmp_v3_auth_protocol || "none"}
                        onChange={(e) =>
                          onChange({ ...draft, snmp_v3_auth_protocol: e.target.value })
                        }
                        className="w-full border dark:bg-gray-900 dark:border-gray-600 dark:text-gray-100 rounded-lg px-3 py-2 text-sm"
                      >
                        {["none", "MD5", "SHA", "SHA224", "SHA256", "SHA384", "SHA512"].map((p) => (
                          <option key={p} value={p}>
                            {p}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-gray-600 dark:text-gray-300 block mb-1">
                        Auth passphrase
                      </label>
                      <input
                        type="password"
                        value={draft.snmp_v3_auth_password || ""}
                        onChange={(e) =>
                          onChange({ ...draft, snmp_v3_auth_password: e.target.value })
                        }
                        placeholder={draft.has_snmp_v3_auth_password ? "•••••• (unchanged)" : ""}
                        disabled={(draft.snmp_v3_auth_protocol || "none") === "none"}
                        className="w-full border dark:bg-gray-900 dark:border-gray-600 dark:text-gray-100 rounded-lg px-3 py-2 text-sm font-mono disabled:bg-gray-100"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-gray-600 dark:text-gray-300 block mb-1">
                        Privacy protocol
                      </label>
                      <select
                        value={draft.snmp_v3_priv_protocol || "none"}
                        onChange={(e) =>
                          onChange({ ...draft, snmp_v3_priv_protocol: e.target.value })
                        }
                        className="w-full border dark:bg-gray-900 dark:border-gray-600 dark:text-gray-100 rounded-lg px-3 py-2 text-sm"
                      >
                        {["none", "DES", "3DES", "AES", "AES192", "AES256"].map((p) => (
                          <option key={p} value={p}>
                            {p}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-gray-600 dark:text-gray-300 block mb-1">
                        Privacy passphrase
                      </label>
                      <input
                        type="password"
                        value={draft.snmp_v3_priv_password || ""}
                        onChange={(e) =>
                          onChange({ ...draft, snmp_v3_priv_password: e.target.value })
                        }
                        placeholder={draft.has_snmp_v3_priv_password ? "•••••• (unchanged)" : ""}
                        disabled={(draft.snmp_v3_priv_protocol || "none") === "none"}
                        className="w-full border dark:bg-gray-900 dark:border-gray-600 dark:text-gray-100 rounded-lg px-3 py-2 text-sm font-mono disabled:bg-gray-100"
                      />
                    </div>
                  </div>
                  <p className="text-[11px] text-gray-400">
                    Privacy (encryption) requires an auth protocol — that's SNMP v3{" "}
                    <em>authPriv</em>.
                  </p>
                </div>
              )}
            </fieldset>
          );
        })()}
        <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-3 mb-3 space-y-3">
          <div className="text-xs font-semibold text-gray-600 dark:text-gray-300">
            SSH credentials
            <span className="font-normal text-gray-400">
              {" "}
              — used to push config (enable/disable ports, VLANs) and the SSH console. The password
              is encrypted at rest and never shown again.
            </span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="sm:col-span-1">
              <label className="text-xs font-semibold text-gray-600 dark:text-gray-300 block mb-1">Username</label>
              <input
                type="text"
                value={draft.ssh_username || ""}
                onChange={(e) => onChange({ ...draft, ssh_username: e.target.value })}
                placeholder="admin"
                autoComplete="off"
                className="w-full border dark:bg-gray-900 dark:border-gray-600 dark:text-gray-100 rounded-lg px-3 py-2 text-sm font-mono"
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-600 dark:text-gray-300 block mb-1">Password</label>
              <input
                type="password"
                value={draft.ssh_password || ""}
                onChange={(e) => onChange({ ...draft, ssh_password: e.target.value })}
                placeholder={draft.has_ssh_credentials ? "•••••• (unchanged)" : ""}
                autoComplete="new-password"
                className="w-full border dark:bg-gray-900 dark:border-gray-600 dark:text-gray-100 rounded-lg px-3 py-2 text-sm font-mono"
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-600 dark:text-gray-300 block mb-1">SSH port</label>
              <input
                type="number"
                value={draft.ssh_port || 22}
                onChange={(e) => onChange({ ...draft, ssh_port: Number(e.target.value) })}
                className="w-full border dark:bg-gray-900 dark:border-gray-600 dark:text-gray-100 rounded-lg px-3 py-2 text-sm"
              />
            </div>
          </div>
        </div>
        <label className="flex items-center gap-2 text-xs mb-4 cursor-pointer">
          <input
            type="checkbox"
            checked={draft.enabled !== false}
            onChange={(e) => onChange({ ...draft, enabled: e.target.checked })}
          />
          <span>Enabled (polled every 5 minutes)</span>
        </label>
        <div className="flex items-center justify-end gap-3">
          <button
            onClick={onCancel}
            className="text-sm border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 px-4 py-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700/40"
          >
            Cancel
          </button>
          <button
            onClick={onSave}
            disabled={!draft.name || !draft.ip_address || (!draft.id && !draft.customer_id)}
            className="text-sm bg-blue-600 text-white px-4 py-1.5 rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            {draft.id ? "Save changes" : "Add device"}
          </button>
        </div>
      </div>
    </div>
  );
}

function DetailModal({
  device,
  onClose,
  onPoll,
  onEdit,
  canManage,
}: {
  device: NetworkDevice;
  onClose: () => void;
  onPoll: () => void;
  onEdit: () => void;
  canManage: boolean;
}) {
  const isSwitch = device.kind === "switch";
  // Switches, routers, firewalls, and APs all have an interface faceplate
  // (built from the IF-MIB port table). Only switches get the write actions
  // (the port CLI is Cisco switchport-style).
  const hasFaceplate = ["switch", "router", "firewall", "access_point"].includes(device.kind);
  // A DEVICE WITH A PROBLEM OPENS ON THE PROBLEM. The faceplate is the right
  // landing tab for healthy gear, but opening a failing access point on
  // "Interfaces" led with "No interface data yet" — a consequence of the
  // fault — while the reason it is down, and what to do about it, sat one
  // click away on Settings.
  const [tab, setTab] = useState<"settings" | "switch" | "hardware" | "ssh" | "ai">(
    hasFaceplate && !device.last_error ? "switch" : "settings",
  );
  const uptimeHuman = (sec?: number | null) => {
    if (!sec) return "—";
    const days = Math.floor(sec / 86400);
    const hours = Math.floor((sec % 86400) / 3600);
    const mins = Math.floor((sec % 3600) / 60);
    return `${days}d ${hours}h ${mins}m`;
  };
  const canReboot =
    ["switch", "router", "firewall"].includes(device.kind) && device.has_ssh_credentials;
  const [rebooting, setRebooting] = useState(false);
  const rebootDevice = async () => {
    const typed = window.prompt(
      `Reboot ${device.name} now?\n\nThis reloads the device over SSH — it will drop offline for ` +
        `a minute or two and interrupt everything behind it. Type the device name (${device.name}) to confirm.`,
    );
    if (typed === null) return;
    setRebooting(true);
    try {
      await api.post(`/api/v1/network-devices/${device.id}/reboot`, { confirm_name: typed });
      window.alert(
        `Reboot sent to ${device.name}. It runs the Cisco 'reload' sequence over the collector ` +
          `and will drop offline shortly. (If this device isn't Cisco-family, use the SSH console ` +
          `to send its vendor's reboot command instead.)`,
      );
    } catch (e: any) {
      window.alert(e.response?.data?.detail || "Failed to send reboot");
    } finally {
      setRebooting(false);
    }
  };
  /**
   * Vendor override — say what the device is, when it will not say itself.
   *
   * 27 of this fleet's network devices are unidentified, and every one of
   * them has neither a sysObjectID nor a sysDescr: they are ICMP-only or
   * silent to SNMP, so no better detection reaches them. This is the place
   * an operator who KNOWS records it.
   *
   * Local state seeded from the device and refreshed from the PUT response
   * rather than from a parent reload, so the identification line and the
   * refusal notice below it update the moment the choice is made - the
   * whole point being to see the consequence of what you just said.
   */
  const [ident, setIdent] = useState({
    override: device.vendor_override || "",
    label: device.vendor_label || "",
    matchedOn: device.vendor_matched_on || "",
    refusal: device.config_push_refusal || "",
  });
  const [families, setFamilies] = useState<
    { family: string; label: string; config_push: boolean }[]
  >([]);
  const [vendorBusy, setVendorBusy] = useState(false);
  useEffect(() => {
    if (!canManage) return;
    // From the driver registry, never a hardcoded list here: a family added
    // to the backend must appear without a matching frontend edit.
    api
      .get("/api/v1/network-devices/vendor-families")
      .then((r) => setFamilies(r.data || []))
      .catch(() => setFamilies([]));
  }, [canManage]);
  const saveVendorOverride = async (value: string) => {
    const previous = ident.override;
    setVendorBusy(true);
    setIdent((s) => ({ ...s, override: value }));
    try {
      const r = await api.put(`/api/v1/network-devices/${device.id}`, {
        vendor_override: value,
      });
      const d = r.data || {};
      setIdent({
        override: d.vendor_override || "",
        label: d.vendor_label || "",
        matchedOn: d.vendor_matched_on || "",
        refusal: d.config_push_refusal || "",
      });
    } catch (e: any) {
      setIdent((s) => ({ ...s, override: previous })); // put the control back
      window.alert(e.response?.data?.detail || "Could not save the vendor family");
    } finally {
      setVendorBusy(false);
    }
  };

  const [maint, setMaint] = useState<boolean>(!!device.maintenance_mode);
  const [maintBusy, setMaintBusy] = useState(false);
  const toggleMaint = async () => {
    setMaintBusy(true);
    try {
      const r = await api.post(`/api/v1/network-devices/${device.id}/maintenance`, {
        enabled: !maint,
      });
      setMaint(!!r.data.maintenance_mode);
    } catch (e: any) {
      window.alert(e.response?.data?.detail || "Failed to toggle maintenance mode");
    } finally {
      setMaintBusy(false);
    }
  };
  const tabs: { key: typeof tab; label: string }[] = [
    { key: "settings", label: "Settings" },
    ...(hasFaceplate
      ? [{ key: "switch" as const, label: isSwitch ? "Switch" : "Interfaces" }]
      : []),
    { key: "hardware", label: "Hardware" },
    { key: "ssh", label: "SSH console" },
    { key: "ai", label: "🤖 AI" },
  ];
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 dark:text-gray-100 rounded-xl shadow-xl w-full max-w-5xl p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h2 className="text-lg font-semibold">{device.name}</h2>
            <div className="text-xs text-gray-500 dark:text-gray-400 font-mono">
              {device.ip_address}:{device.snmp_port} · SNMP {device.snmp_version} ·{" "}
              {kindBadge(device.kind)}
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 dark:hover:text-gray-200">
            ✕
          </button>
        </div>

        <div className="flex gap-1 border-b border-gray-200 dark:border-gray-700 mb-4">
          {tabs.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`text-sm px-4 py-2 -mb-px border-b-2 ${
                tab === t.key
                  ? "border-blue-600 text-blue-700 dark:text-blue-400 font-semibold"
                  : "border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === "settings" && (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm mb-4">
              <div>
                <span className="text-gray-500 dark:text-gray-400 text-xs uppercase">Status</span>
                <div className="mt-1">{statusBadge(device.last_status)}</div>
              </div>
              <div>
                <span className="text-gray-500 dark:text-gray-400 text-xs uppercase">Customer</span>
                <div className="mt-1">{device.customer_name || "—"}</div>
              </div>
              <div>
                <span className="text-gray-500 dark:text-gray-400 text-xs uppercase">sysName</span>
                <div className="mt-1">{device.sys_name || "—"}</div>
              </div>
              <div>
                <span className="text-gray-500 dark:text-gray-400 text-xs uppercase">Uptime</span>
                <div className="mt-1">{uptimeHuman(device.sys_uptime_seconds)}</div>
              </div>
              <div>
                <span className="text-gray-500 dark:text-gray-400 text-xs uppercase">Consec. failures</span>
                {/* Shown as a floor at the ceiling. The counter used to
                    increment once per AGENT per cycle rather than once per
                    cycle, so historical rows reached 67,999; those were
                    rebuilt to a capped estimate and it would be dishonest to
                    render the cap as an exact count. */}
                <div className="mt-1 font-mono" title="Consecutive failed poll cycles">
                  {device.consecutive_failures >= 2016
                    ? "2016+"
                    : device.consecutive_failures}
                </div>
              </div>
              <div>
                <span className="text-gray-500 dark:text-gray-400 text-xs uppercase">SSH credentials</span>
                <div className="mt-1">
                  {device.has_ssh_credentials ? (
                    <span className="text-green-700 dark:text-green-400">
                      ✓ {device.ssh_username}@{device.ip_address}:{device.ssh_port || 22}
                    </span>
                  ) : (
                    <span className="text-gray-400">Not configured</span>
                  )}
                </div>
              </div>
              <div className="col-span-2">
                <span className="text-gray-500 dark:text-gray-400 text-xs uppercase">sysDescr</span>
                <div className="mt-1 text-xs font-mono bg-gray-50 dark:bg-gray-900 border dark:border-gray-700 rounded p-2 max-h-32 overflow-y-auto">
                  {device.sys_descr || "—"}
                </div>
              </div>
              {device.last_error && (
                <div className="col-span-2">
                  <span className="text-gray-500 dark:text-gray-400 text-xs uppercase">Last error</span>
                  <div className="mt-1 text-xs font-mono bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded p-2 text-red-700 dark:text-red-400">
                    {device.last_error}
                  </div>
                  {device.error_diagnosis && (
                    <div className="mt-2">
                      <SnmpDiagnosisPanel d={device.error_diagnosis} />
                    </div>
                  )}
                </div>
              )}
              {/* What the driver gate identified this device as. Computed
                  server-side since the vendor work shipped and never shown,
                  so a refused config push had no visible reason. */}
              <div className="col-span-2">
                <span className="text-gray-500 dark:text-gray-400 text-xs uppercase">
                  Identified as
                </span>
                <div className="mt-1 text-xs text-gray-800 dark:text-gray-200">
                  <span className="font-medium">{ident.label || "Unidentified"}</span>
                  {ident.matchedOn ? (
                    <span className="text-gray-500 dark:text-gray-400"> — {ident.matchedOn}</span>
                  ) : null}
                </div>
                {device.sys_object_id ? (
                  <div className="mt-0.5 font-mono text-[11px] text-gray-500 dark:text-gray-400">
                    sysObjectID {device.sys_object_id}
                  </div>
                ) : null}
                {/* SAY WHAT IT IS. A device with no sysObjectID and no
                    sysDescr cannot be identified by any amount of cleverness
                    - it is never asked. Before this the only way past the
                    gate was an admin-only per-push "force", which asserts
                    "ignore the check" instead of supplying the missing fact,
                    and records nothing for the next person. */}
                {canManage && (
                  <div className="mt-2">
                    <label className="block text-[11px] text-gray-500 dark:text-gray-400 mb-1">
                      Vendor family
                    </label>
                    <select
                      value={ident.override}
                      disabled={vendorBusy}
                      onChange={(e) => void saveVendorOverride(e.target.value)}
                      className="w-full border rounded px-2 py-1 text-xs dark:bg-gray-900 dark:border-gray-600 disabled:opacity-50"
                    >
                      <option value="">Detect automatically</option>
                      {families.map((f) => (
                        <option key={f.family} value={f.family}>
                          {f.label}
                          {f.config_push ? "" : " (monitoring only)"}
                        </option>
                      ))}
                    </select>
                    <p className="mt-1 text-[11px] text-gray-500 dark:text-gray-400">
                      {vendorBusy
                        ? "Saving…"
                        : ident.override
                          ? "Set by hand — this overrides what the device reports."
                          : "Naming the family does not by itself allow config push; " +
                            "families we cannot drive safely stay monitoring-only."}
                    </p>
                  </div>
                )}
                {ident.refusal ? (
                  <div className="mt-1.5 text-[11px] text-amber-800 dark:text-amber-200 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800/60 rounded p-2">
                    {ident.refusal}
                  </div>
                ) : null}
              </div>
            </div>
            {canManage && (
              <div className="flex items-center justify-end gap-3">
                {canReboot && (
                  <button
                    onClick={rebootDevice}
                    disabled={rebooting}
                    className="text-sm border border-orange-300 dark:border-orange-700/60 text-orange-700 dark:text-orange-300 px-4 py-1.5 rounded-lg hover:bg-orange-50 dark:hover:bg-orange-900/30 disabled:opacity-50 mr-auto"
                    title="Reboot this device over SSH (Cisco reload)"
                  >
                    {rebooting ? "Sending…" : "⟲ Reboot device"}
                  </button>
                )}
                <button
                  onClick={toggleMaint}
                  disabled={maintBusy}
                  className={`text-sm px-4 py-1.5 rounded-lg font-medium text-white disabled:opacity-50 ${maint ? "bg-amber-600 hover:bg-amber-700" : "bg-teal-600 hover:bg-teal-700"} ${canReboot ? "" : "mr-auto"}`}
                  title={
                    maint
                      ? "Exit maintenance — re-enable alerting for this device"
                      : "Enter maintenance — mute all alerting for this device"
                  }
                >
                  🔧 {maintBusy ? "…" : maint ? "Exit maintenance" : "Maintenance mode"}
                </button>
                <button
                  onClick={onEdit}
                  className="text-sm border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 px-4 py-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700/40"
                >
                  Edit settings & credentials
                </button>
                <button
                  onClick={onPoll}
                  className="text-sm bg-blue-600 text-white px-4 py-1.5 rounded-lg hover:bg-blue-700"
                >
                  Poll now
                </button>
              </div>
            )}
          </>
        )}

        {tab === "switch" && hasFaceplate && <SwitchPanel device={device} canManage={canManage} />}

        {tab === "hardware" && <HardwarePanel device={device} />}

        {tab === "ssh" && <SshConsole device={device} canManage={canManage} onEdit={onEdit} />}

        {tab === "ai" && <NetworkAIChat device={device} canManage={canManage} />}
      </div>
    </div>
  );
}

// ---------- Hardware tab ----------

type HardwareData = {
  id: number;
  kind: string;
  monitor_type?: string;
  last_status: string;
  last_polled_at?: string | null;
  sys_name?: string | null;
  sys_descr?: string | null;
  sys_uptime_seconds?: number | null;
  chassis: { model?: string | null; serial?: string | null; firmware?: string | null };
  /** One entry per stack member. A standalone switch has one or none. */
  stack?: {
    index?: string;
    name?: string | null;
    model?: string | null;
    serial?: string | null;
    firmware?: string | null;
    /** Dell Service Tag. On this hardware it is the only per-unit
     *  identifier ENTITY-MIB reports correctly. */
    asset_tag?: string | null;
  }[];
  mgmt_ips: string[];
  /** `stack_unit` is which member it belongs to; `unit` on a sensor is the
   *  unit of MEASURE and is a different thing entirely. */
  power_supplies: { name: string; status?: string | null; stack_unit?: string | null }[];
  fans: {
    name: string;
    status?: string | null;
    rpm?: number | null;
    stack_unit?: string | null;
  }[];
  sensors: {
    name: string;
    type: string;
    value: any;
    unit?: string;
    status?: string | null;
    stack_unit?: string | null;
  }[];
  collected: boolean;
};

function hwStatusDot(status?: string | null) {
  const ok = status === "ok";
  const bad = status === "nonoperational";
  const cls = ok ? "bg-green-500" : bad ? "bg-red-500" : "bg-gray-400";
  return <span className={`inline-block w-2 h-2 rounded-full ${cls}`} />;
}

type NetChatMsg = { role: "user" | "assistant" | "output"; content: string; command?: string | null };

function NetworkAIChat({
  device,
  canManage,
}: {
  device: NetworkDevice;
  canManage: boolean;
}) {
  const [messages, setMessages] = useState<NetChatMsg[]>([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [running, setRunning] = useState(false);
  const [err, setErr] = useState("");
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, busy]);

  const apiHistory = (msgs: NetChatMsg[]) =>
    msgs.filter((m) => m.role !== "output").map((m) => ({ role: m.role, content: m.content }));

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setErr("");
    const next = [...messages, { role: "user" as const, content: text }];
    setMessages(next);
    setInput("");
    setBusy(true);
    try {
      const r = await api.post(`/api/v1/ai/network-devices/${device.id}/chat`, {
        messages: apiHistory(next),
      });
      setMessages((m) => [
        ...m,
        { role: "assistant", content: r.data.reply, command: r.data.suggested_command },
      ]);
    } catch (e: any) {
      setErr(e.response?.data?.detail || "The assistant couldn't respond.");
    } finally {
      setBusy(false);
    }
  };

  const runCommand = async (command: string) => {
    if (!window.confirm(`Run this on ${device.name} over SSH?\n\n${command}`)) return;
    setRunning(true);
    setErr("");
    try {
      const q = await api.post(`/api/v1/ai/network-devices/${device.id}/chat/run`, { command });
      const commandId = q.data.command_id;
      const started = Date.now();
      const poll = async (): Promise<void> => {
        const r = await api.get(`/api/v1/network-devices/${device.id}/command/${commandId}`);
        const st = r.data?.status;
        if (st === "completed" || st === "failed") {
          const tr = (r.data?.result?.transcript || []) as { command: string; output: string }[];
          const out =
            tr.map((t) => `${t.command}\n${t.output}`).join("\n").trim() ||
            r.data?.error_message ||
            "(no output)";
          setMessages((m) => [...m, { role: "output", content: out }]);
          setRunning(false);
          return;
        }
        if (Date.now() - started > 90000) {
          setMessages((m) => [
            ...m,
            { role: "output", content: "Timed out — the device may be busy or the collector offline." },
          ]);
          setRunning(false);
          return;
        }
        setTimeout(poll, 2500);
      };
      setTimeout(poll, 2000);
    } catch (e: any) {
      setErr(e.response?.data?.detail || "Couldn't run the command.");
      setRunning(false);
    }
  };

  return (
    <div className="flex flex-col h-[26rem]">
      <div ref={scrollRef} className="flex-1 overflow-y-auto space-y-3 pr-1">
        {messages.length === 0 && (
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Ask about this device or what you'd like to change — e.g. "show the VLAN config" or
            "which ports are down?". I'll propose CLI you can review and run over SSH.
          </p>
        )}
        {messages.map((m, i) =>
          m.role === "output" ? (
            <pre
              key={i}
              className="text-xs bg-gray-900 text-green-200 rounded-lg p-3 overflow-x-auto whitespace-pre-wrap"
            >
              {m.content}
            </pre>
          ) : (
            <div
              key={i}
              className={`text-sm rounded-lg px-3 py-2 ${
                m.role === "user"
                  ? "bg-indigo-600 text-white ml-8"
                  : "bg-gray-100 dark:bg-gray-700/60 text-gray-800 dark:text-gray-100 mr-4"
              }`}
            >
              <div className="whitespace-pre-wrap break-words">{m.content}</div>
              {m.role === "assistant" && m.command && (
                <div className="mt-2">
                  <pre className="text-xs bg-gray-900 text-gray-100 rounded p-2 overflow-x-auto whitespace-pre-wrap">
                    {m.command}
                  </pre>
                  <div className="flex items-center gap-2 mt-1">
                    {canManage ? (
                      <button
                        onClick={() => runCommand(m.command as string)}
                        disabled={running}
                        className="text-xs bg-amber-600 text-white px-2.5 py-1 rounded hover:bg-amber-700 disabled:opacity-50"
                      >
                        {running ? "Running…" : "▶ Run over SSH"}
                      </button>
                    ) : (
                      <span className="text-xs text-gray-400">Admins can run commands</span>
                    )}
                    <button
                      onClick={() => navigator.clipboard?.writeText(m.command as string)}
                      className="text-xs border border-gray-300 dark:border-gray-600 px-2.5 py-1 rounded hover:bg-gray-50 dark:hover:bg-gray-700/40"
                    >
                      Copy
                    </button>
                  </div>
                </div>
              )}
            </div>
          ),
        )}
        {busy && <div className="text-xs text-gray-400 italic">Thinking…</div>}
      </div>
      {err && <p className="text-xs text-red-600 dark:text-red-400 mt-1">{err}</p>}
      <div className="flex items-end gap-2 mt-2">
        <textarea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              send();
            }
          }}
          rows={2}
          placeholder="Ask about this device…"
          className="flex-1 border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-3 py-2 text-sm resize-none"
        />
        <button
          onClick={send}
          disabled={busy || !input.trim()}
          className="bg-indigo-600 text-white text-sm px-4 py-2 rounded-lg hover:bg-indigo-700 disabled:opacity-50"
        >
          Send
        </button>
      </div>
    </div>
  );
}

function HardwarePanel({ device }: { device: NetworkDevice }) {
  const [hw, setHw] = useState<HardwareData | null>(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api
      .get(`/api/v1/network-devices/${device.id}/hardware`)
      .then((r) => alive && setHw(r.data))
      .catch((e) => alive && setErr(e.response?.data?.detail || e.message))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [device.id]);

  if (loading) return <div className="text-sm text-gray-500 dark:text-gray-400 py-6">Loading hardware…</div>;
  if (err) return <div className="text-sm text-red-600 dark:text-red-400 py-6">{err}</div>;
  if (!hw) return null;

  const temps = hw.sensors.filter((s) => s.type === "temperature");
  const other = hw.sensors.filter((s) => s.type !== "temperature" && s.type !== "fan");
  const model = hw.chassis?.model || null;
  // A stack is several switches sharing one address. One row of "Model /
  // Serial / Firmware" describes only the first of them, which is how five
  // units came to look like one device with one serial number.
  const stack = hw.stack ?? [];
  const isStack = stack.length > 1;
  // Only when the hardware actually publishes one. This Dell reports its
  // per-unit service tags on the CLI but leaves entPhysicalAssetID empty, so
  // an always-dash column would just be furniture.
  const anyAssetTag = stack.some((u) => u.asset_tag);
  // Same rule for the other two. This hardware reports entPhysicalSerialNum
  // as the MANAGEMENT unit's serial on every chassis row (suppressed as a
  // duplicate, and shown once above as the stack serial) and leaves
  // entPhysicalFirmwareRev empty per unit. A column of dashes reads as a
  // broken screen rather than as "the device does not publish this".
  const anyUnitSerial = stack.some((u) => u.serial);
  const anyUnitFirmware = stack.some((u) => u.firmware);
  // Firmware often is not in ENTITY-MIB at all, but the device announces it
  // in sysDescr - "Dell EMC Networking N2048P, 6.7.0.4, Linux 4.14.174". A
  // value we already hold and do not show is the same defect as not
  // collecting it. Only ever a FALLBACK: a real entPhysicalFirmwareRev wins.
  const firmware =
    hw.chassis?.firmware ||
    (hw.sys_descr?.match(/(\d+\.\d+\.\d+(?:\.\d+)?)/) || [])[1] ||
    null;
  // Which member a component sits in. Without this, "PSU 2 failed" on a
  // five-unit stack does not tell you which switch to walk up to.
  const UnitChip = ({ unit }: { unit?: string | null }) =>
    isStack && unit ? (
      <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 whitespace-nowrap">
        {unit}
      </span>
    ) : null;

  const Info = ({ label, value }: { label: string; value: React.ReactNode }) => (
    <div>
      <span className="text-gray-500 dark:text-gray-400 text-xs uppercase">{label}</span>
      <div className="mt-0.5 text-sm text-gray-900 dark:text-gray-100 break-words">
        {value || "—"}
      </div>
    </div>
  );

  return (
    <div className="space-y-5">
      {/* Device information */}
      <section>
        <h3 className="text-xs font-semibold uppercase text-gray-400 mb-2">Device information</h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
          <Info
            label="Model"
            value={
              model || (
                <span className="text-gray-400">
                  {hw.sys_descr ? hw.sys_descr.slice(0, 40) : "—"}
                </span>
              )
            }
          />
          {/* On a stack this is the serial the device reports for ITSELF -
              the management unit's. Show it, and say what it is: replacing
              it with a pointer left the screen with no serial anywhere,
              which is worse than the duplication it was meant to fix. */}
          <Info
            label={isStack ? "Serial (stack)" : "Serial"}
            value={
              hw.chassis?.serial ? (
                <span title={isStack
                  ? "Reported by the stack as a whole. Per-unit service tags, where the hardware publishes them, are listed below."
                  : undefined}>
                  <span className="font-mono text-xs">{hw.chassis.serial}</span>
                </span>
              ) : null
            }
          />
          <Info label="Firmware" value={firmware} />
          <Info
            label="Management IP"
            value={hw.mgmt_ips?.length ? hw.mgmt_ips.join(", ") : device.ip_address}
          />
          <Info label="sysName" value={hw.sys_name} />
          <Info label="Monitoring" value={hw.monitor_type === "icmp" ? "ICMP ping" : "SNMP"} />
        </div>
      </section>

      {/* Stack members. Only when there is more than one - a standalone
          switch already has everything it needs in the block above, and a
          one-row table saying the same thing again is just noise. */}
      {isStack && (
        <section>
          <h3 className="text-xs font-semibold uppercase text-gray-400 mb-2">
            Stack members ({stack.length})
          </h3>
          <div className="overflow-x-auto">
            <table className="min-w-[560px] w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase text-gray-400 border-b border-gray-200 dark:border-gray-700">
                  <th className="py-1.5 pr-3 font-medium">Unit</th>
                  <th className="py-1.5 pr-3 font-medium">Model</th>
                  {anyAssetTag && (
                    <th className="py-1.5 pr-3 font-medium">Service tag</th>
                  )}
                  {anyUnitSerial && (
                    <th className="py-1.5 pr-3 font-medium">Serial</th>
                  )}
                  {anyUnitFirmware && (
                    <th className="py-1.5 pr-3 font-medium">Firmware</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {stack.map((u, i) => (
                  <tr
                    key={u.index ?? i}
                    className="border-b border-gray-100 dark:border-gray-800 last:border-0"
                  >
                    <td className="py-1.5 pr-3 font-medium text-gray-900 dark:text-gray-100">
                      {u.name || `Unit ${i + 1}`}
                    </td>
                    <td className="py-1.5 pr-3 text-gray-700 dark:text-gray-300">
                      {u.model || "—"}
                    </td>
                    {anyAssetTag && (
                      <td className="py-1.5 pr-3 font-mono text-xs text-gray-700 dark:text-gray-300">
                        {u.asset_tag || "—"}
                      </td>
                    )}
                    {anyUnitSerial && (
                      <td className="py-1.5 pr-3 font-mono text-xs text-gray-700 dark:text-gray-300">
                        {u.serial || "—"}
                      </td>
                    )}
                    {anyUnitFirmware && (
                      <td className="py-1.5 pr-3 text-gray-700 dark:text-gray-300">
                        {u.firmware || "—"}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {!anyUnitSerial && !anyUnitFirmware && !anyAssetTag && (
            <p className="mt-1.5 text-[11px] text-gray-500 dark:text-gray-400">
              This stack reports one serial and firmware for the whole unit over
              SNMP rather than per member — both are shown above. Per-unit
              service tags exist on the switch itself but are not published to
              SNMP.
            </p>
          )}
        </section>
      )}

      {!hw.collected && (
        <div className="text-xs text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-3">
          No detailed hardware inventory reported yet. The on-site collector walks ENTITY-MIB /
          ENTITY-SENSOR-MIB on its next poll; not all devices expose fans/PSU/temperature sensors
          over SNMP.
        </div>
      )}

      {/* Power supplies + fans */}
      {(hw.power_supplies.length > 0 || hw.fans.length > 0) && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <section>
            <h3 className="text-xs font-semibold uppercase text-gray-400 mb-2">
              Power supplies ({hw.power_supplies.length})
            </h3>
            <div className="space-y-1.5">
              {hw.power_supplies.length === 0 && <div className="text-sm text-gray-400">—</div>}
              {hw.power_supplies.map((p, i) => (
                <div key={i} className="flex items-center gap-2 text-sm">
                  {hwStatusDot(p.status)}
                  <UnitChip unit={p.stack_unit} />
                  <span className="text-gray-800 dark:text-gray-100">{p.name}</span>
                  {p.status && <span className="text-xs text-gray-400">({p.status})</span>}
                </div>
              ))}
            </div>
          </section>
          <section>
            <h3 className="text-xs font-semibold uppercase text-gray-400 mb-2">
              Fans ({hw.fans.length})
            </h3>
            <div className="space-y-1.5">
              {hw.fans.length === 0 && <div className="text-sm text-gray-400">—</div>}
              {hw.fans.map((f, i) => (
                <div key={i} className="flex items-center gap-2 text-sm">
                  {hwStatusDot(f.status)}
                  <UnitChip unit={f.stack_unit} />
                  <span className="text-gray-800 dark:text-gray-100">{f.name}</span>
                  {f.rpm != null && <span className="text-xs text-gray-500 dark:text-gray-400">{f.rpm} rpm</span>}
                </div>
              ))}
            </div>
          </section>
        </div>
      )}

      {/* Temperatures */}
      {temps.length > 0 && (
        <section>
          <h3 className="text-xs font-semibold uppercase text-gray-400 mb-2">Temperatures</h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {temps.map((s, i) => (
              <div key={i} className="rounded-lg border border-gray-200 dark:border-gray-700 p-2.5">
                <div className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
                  {hwStatusDot(s.status)}
                  <UnitChip unit={s.stack_unit} />
                  <span className="truncate">{s.name}</span>
                </div>
                <div className="mt-1 text-lg font-semibold text-gray-900 dark:text-gray-100">
                  {s.value}
                  <span className="text-sm text-gray-500 dark:text-gray-400">°{s.unit || "C"}</span>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Other sensors (voltage/current/power/etc.) */}
      {other.length > 0 && (
        <section>
          <h3 className="text-xs font-semibold uppercase text-gray-400 mb-2">Other sensors</h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-sm">
            {other.map((s, i) => (
              <div
                key={i}
                className="flex items-center justify-between gap-2 rounded border border-gray-100 dark:border-gray-700 px-2 py-1"
              >
                <span className="flex items-center gap-1.5 text-gray-700 dark:text-gray-200 truncate">
                  {hwStatusDot(s.status)}
                  {s.name}
                </span>
                <span className="font-mono text-gray-900 dark:text-gray-100 whitespace-nowrap">
                  {s.value}
                  {s.unit ? ` ${s.unit}` : ""}
                </span>
              </div>
            ))}
          </div>
        </section>
      )}

      <div className="text-[11px] text-gray-400">
        Last polled: {hw.last_polled_at ? formatDateTime(hw.last_polled_at) : "never"}
      </div>
    </div>
  );
}

type SshStatus = "idle" | "creating" | "connecting" | "active" | "closed" | "error";

function SshConsole({
  device,
  canManage,
  onEdit,
}: {
  device: NetworkDevice;
  canManage: boolean;
  onEdit: () => void;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const termRef = useRef<Terminal | null>(null);
  const fitRef = useRef<FitAddon | null>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const [status, setStatus] = useState<SshStatus>("idle");
  const startable = canManage && !!device.has_ssh_credentials;

  useEffect(() => {
    if (!startable) return;
    let cancelled = false;
    let term: Terminal | null = null;
    let ws: WebSocket | null = null;

    const sendResize = () => {
      const t = termRef.current;
      if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN || !t) return;
      try {
        wsRef.current.send(JSON.stringify({ type: "resize", cols: t.cols, rows: t.rows }));
      } catch {
        /* ignore */
      }
    };
    const safeFit = () => {
      const el = containerRef.current;
      const fit = fitRef.current;
      if (!fit || !el || el.clientWidth === 0 || el.clientHeight === 0) return;
      try {
        fit.fit();
      } catch {
        requestAnimationFrame(safeFit);
      }
    };

    const setup = async () => {
      term = new Terminal({
        convertEol: true,
        cursorBlink: true,
        fontFamily: 'Consolas, "Cascadia Mono", "JetBrains Mono", monospace',
        fontSize: 13,
        theme: { background: "#0a0e1a", foreground: "#e6edf3" },
        scrollback: 5000,
      });
      const fit = new FitAddon();
      term.loadAddon(fit);
      termRef.current = term;
      fitRef.current = fit;
      if (containerRef.current) {
        term.open(containerRef.current);
        requestAnimationFrame(() => requestAnimationFrame(safeFit));
      }
      term.writeln(`\x1b[90m[ssh] opening session to ${device.ip_address} via collector…\x1b[0m`);
      setStatus("creating");

      let token: string;
      try {
        const r = await api.post(`/api/v1/shell/network-sessions`, {
          network_device_id: device.id,
        });
        token = r.data.token;
      } catch (err: any) {
        if (cancelled) return;
        term?.writeln(`\x1b[31m[ssh] ${err.response?.data?.detail || err.message}\x1b[0m`);
        setStatus("error");
        return;
      }
      if (cancelled) return;
      setStatus("connecting");
      term?.writeln("\x1b[90m[ssh] waiting for the collector to dial out…\x1b[0m");

      const jwt = localStorage.getItem("token") || "";
      const wsProto = window.location.protocol === "https:" ? "wss:" : "ws:";
      ws = new WebSocket(
        `${wsProto}//${window.location.host}/api/v1/shell/ws/${token}?access_token=${encodeURIComponent(jwt)}`,
      );
      ws.binaryType = "arraybuffer";
      wsRef.current = ws;

      ws.onopen = () => {
        if (cancelled) return;
        setStatus("active");
        term?.focus();
        sendResize();
      };
      ws.onmessage = (evt) => {
        if (!term) return;
        if (evt.data instanceof ArrayBuffer) term.write(new Uint8Array(evt.data));
        else if (typeof evt.data === "string") term.write(evt.data);
      };
      ws.onerror = () => {
        if (!cancelled) {
          term?.writeln("\r\n\x1b[31m[ssh] WebSocket error.\x1b[0m");
          setStatus("error");
        }
      };
      ws.onclose = (evt) => {
        if (!cancelled) {
          term?.writeln(
            `\r\n\x1b[90m[ssh] disconnected (${evt.reason || `code ${evt.code}`}).\x1b[0m`,
          );
          setStatus("closed");
        }
      };

      const enc = new TextEncoder();
      term.onData((data) => {
        if (ws && ws.readyState === WebSocket.OPEN) ws.send(enc.encode(data));
      });
    };

    // Defer the actual session creation by a tick. Under React StrictMode (dev
    // server) the effect runs → cleanup → runs again; clearing this timer in the
    // cleanup means the throwaway first mount never POSTs, so exactly ONE session
    // (and one queued open_ssh_terminal command) is created per open — no orphan
    // sessions left for the collector to dial into and time out on.
    const startTimer = window.setTimeout(setup, 50);

    let resizeTimer: number | undefined;
    const onResize = () => {
      if (resizeTimer) window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(() => {
        safeFit();
        sendResize();
      }, 150);
    };
    window.addEventListener("resize", onResize);

    return () => {
      cancelled = true;
      window.clearTimeout(startTimer);
      window.removeEventListener("resize", onResize);
      try {
        ws?.close();
      } catch {
        /* ignore */
      }
      try {
        term?.dispose();
      } catch {
        /* ignore */
      }
      wsRef.current = null;
      termRef.current = null;
      fitRef.current = null;
    };
  }, [startable, device.id, device.ip_address]);

  if (!canManage)
    return (
      <div className="text-xs text-gray-500 dark:text-gray-400 py-6">Admin role required for the SSH terminal.</div>
    );
  if (!device.has_ssh_credentials)
    return (
      <div className="text-sm text-gray-600 dark:text-gray-300 py-6">
        <p className="mb-3">
          This device has no SSH credentials. Add an SSH username + password to open an interactive
          terminal and push config.
        </p>
        <button
          onClick={onEdit}
          className="text-sm bg-blue-600 text-white px-4 py-1.5 rounded-lg hover:bg-blue-700"
        >
          Add SSH credentials
        </button>
      </div>
    );

  const badge: Record<SshStatus, { label: string; cls: string }> = {
    idle: { label: "Starting…", cls: "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300" },
    creating: { label: "Creating session…", cls: "bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200" },
    connecting: { label: "Waiting for collector…", cls: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300" },
    active: { label: "Connected", cls: "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300" },
    closed: { label: "Disconnected", cls: "bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-200" },
    error: { label: "Error", cls: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300" },
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <p className="text-[11px] text-gray-400">
          Live interactive SSH to <span className="font-mono">{device.ip_address}</span> tunneled
          through the on-site collector. Every keystroke is audit-logged.
        </p>
        <span className={`text-xs px-2 py-1 rounded-full font-medium ${badge[status].cls}`}>
          {badge[status].label}
        </span>
      </div>
      <div
        ref={containerRef}
        className="rounded-lg overflow-hidden"
        style={{ backgroundColor: "#0a0e1a", height: "60vh", padding: "10px" }}
      />
    </div>
  );
}

// Deterministic VLAN→color so a port and its VLAN row share a swatch.
// REPORTED: "some of the VLAN descriptions / colors at the bottom of the
// switch GUI are hard to read in dark mode." These were the only chips on
// the page with no dark variant - a pale bg-*-100 fill with *-800 text is
// designed for a white card, and on the dark one it is a bright blob with
// text too close to its own background. Every other badge in this file
// already pairs a *-900/40 fill with *-200/300 text; these now match.
const VLAN_PALETTE = [
  "bg-blue-100 text-blue-800 border-blue-300 dark:bg-blue-900/40 dark:text-blue-200 dark:border-blue-700",
  "bg-green-100 text-green-800 border-green-300 dark:bg-green-900/40 dark:text-green-200 dark:border-green-700",
  "bg-purple-100 text-purple-800 border-purple-300 dark:bg-purple-900/40 dark:text-purple-200 dark:border-purple-700",
  "bg-amber-100 text-amber-800 border-amber-300 dark:bg-amber-900/40 dark:text-amber-200 dark:border-amber-700",
  "bg-pink-100 text-pink-800 border-pink-300 dark:bg-pink-900/40 dark:text-pink-200 dark:border-pink-700",
  "bg-teal-100 text-teal-800 border-teal-300 dark:bg-teal-900/40 dark:text-teal-200 dark:border-teal-700",
  "bg-indigo-100 text-indigo-800 border-indigo-300 dark:bg-indigo-900/40 dark:text-indigo-200 dark:border-indigo-700",
  "bg-rose-100 text-rose-800 border-rose-300 dark:bg-rose-900/40 dark:text-rose-200 dark:border-rose-700",
  "bg-cyan-100 text-cyan-800 border-cyan-300 dark:bg-cyan-900/40 dark:text-cyan-200 dark:border-cyan-700",
  "bg-lime-100 text-lime-800 border-lime-300 dark:bg-lime-900/40 dark:text-lime-200 dark:border-lime-700",
  "bg-fuchsia-100 text-fuchsia-800 border-fuchsia-300 dark:bg-fuchsia-900/40 dark:text-fuchsia-200 dark:border-fuchsia-700",
  "bg-orange-100 text-orange-800 border-orange-300 dark:bg-orange-900/40 dark:text-orange-200 dark:border-orange-700",
  "bg-sky-100 text-sky-800 border-sky-300 dark:bg-sky-900/40 dark:text-sky-200 dark:border-sky-700",
  "bg-emerald-100 text-emerald-800 border-emerald-300 dark:bg-emerald-900/40 dark:text-emerald-200 dark:border-emerald-700",
  "bg-violet-100 text-violet-800 border-violet-300 dark:bg-violet-900/40 dark:text-violet-200 dark:border-violet-700",
  "bg-yellow-100 text-yellow-800 border-yellow-300 dark:bg-yellow-900/40 dark:text-yellow-200 dark:border-yellow-700",
];
// Saturated counterparts (same hue order as VLAN_PALETTE) for the faceplate
// stripe — the pale bg-*-100 chip colors are invisible on the dark chassis.
const VLAN_BAR = [
  "bg-blue-500",
  "bg-green-500",
  "bg-purple-500",
  "bg-amber-500",
  "bg-pink-500",
  "bg-teal-500",
  "bg-indigo-500",
  "bg-rose-500",
  "bg-cyan-500",
  "bg-lime-500",
  "bg-fuchsia-500",
  "bg-orange-500",
  "bg-sky-500",
  "bg-emerald-500",
  "bg-violet-500",
  "bg-yellow-500",
];
// Human-readable bits/sec for live interface throughput.
function fmtBps(bps?: number | null): string {
  if (bps == null) return "—";
  if (bps < 1000) return `${bps} bps`;
  if (bps < 1e6) return `${(bps / 1e3).toFixed(1)} Kbps`;
  if (bps < 1e9) return `${(bps / 1e6).toFixed(1)} Mbps`;
  return `${(bps / 1e9).toFixed(2)} Gbps`;
}

function SwitchPanel({ device, canManage }: { device: NetworkDevice; canManage: boolean }) {
  const deviceId = device.id;
  const [detail, setDetail] = useState<SwitchDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [selected, setSelected] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  // Tracks the in-flight post-change poll so we can cancel it on unmount or when
  // a new change starts (prevents overlapping pollers and setState-after-unmount).
  const pollRef = useRef<number | null>(null);
  useEffect(
    () => () => {
      if (pollRef.current) window.clearTimeout(pollRef.current);
    },
    [],
  );

  // Staged port editor — the user sets mode + access/native VLAN + tagged VLANs,
  // then clicks Apply to push them together (instead of one change at a time).
  const [editMode, setEditMode] = useState<string>("access");
  const [editVlan, setEditVlan] = useState<number | "">("");
  const [editTagged, setEditTagged] = useState<Set<number>>(new Set());
  // Re-seed the editor from the selected port (and after a poll refreshes the
  // data). A port with any tagged VLANs is treated as trunk; otherwise access.
  useEffect(() => {
    if (selected == null || !detail) return;
    const p = detail.ports.find((x) => x.if_index === selected);
    if (!p) return;
    setEditMode((p.tagged_vlans?.length ?? 0) > 0 ? "trunk" : "access");
    setEditVlan(p.pvid ?? "");
    setEditTagged(new Set(p.tagged_vlans || []));
  }, [selected, detail]);

  const load = useCallback(() => {
    setLoading(true);
    return api
      .get(`/api/v1/network-devices/${deviceId}/switch`)
      .then((r) => setDetail(r.data))
      .catch((e) => setErr(e.response?.data?.detail || "Failed to load switch detail"))
      .finally(() => setLoading(false));
  }, [deviceId]);

  useEffect(() => {
    let alive = true;
    api
      .get(`/api/v1/network-devices/${deviceId}/switch`)
      .then((r) => {
        if (alive) setDetail(r.data);
      })
      .catch((e) => {
        if (alive) setErr(e.response?.data?.detail || "Failed to load switch detail");
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [deviceId]);

  const vlanName = useMemo(() => {
    const m = new Map<number, string>();
    (detail?.vlans || []).forEach((v) => m.set(v.vlan_id, v.name || `VLAN ${v.vlan_id}`));
    return m;
  }, [detail]);

  const sendChange = async (
    port: SwitchPort,
    body: Record<string, unknown>,
    confirmMsg: string,
  ) => {
    if (!window.confirm(confirmMsg + "\n\nConfig changes apply to the live switch. Continue?"))
      return;
    // Cancel any poller left over from a previous change on this panel.
    if (pollRef.current) {
      window.clearTimeout(pollRef.current);
      pollRef.current = null;
    }
    setBusy(true);
    setNotice(null);
    const label = port.name || port.if_index;

    let commandId: number | null = null;
    try {
      const resp = await api.post(`/api/v1/network-devices/${deviceId}/switch/port`, {
        if_index: port.if_index,
        port_name: port.name || String(port.if_index),
        ...body,
      });
      commandId = resp.data?.command_id ?? null;
    } catch (e: any) {
      setNotice(e.response?.data?.detail || "Change failed to queue.");
      setBusy(false);
      return;
    }

    // The change is relayed to the on-site collector, which applies it over SSH
    // and reports back asynchronously. Poll the command's status instead of
    // guessing a fixed delay: the old code fired a single blind 25s refresh, so
    // a relay slower than that left the panel stuck on "refreshing…" forever.
    if (!commandId) {
      // Older backend without a trackable command id — fall back to one refresh.
      setNotice(`Change queued for ${label}. Refreshing…`);
      pollRef.current = window.setTimeout(() => {
        pollRef.current = null;
        load().finally(() => setBusy(false));
      }, 25000);
      return;
    }

    setNotice(`Applying change to ${label} on the live switch…`);
    const startedAt = Date.now();
    const TIMEOUT_MS = 90000;
    const POLL_MS = 3000;

    const poll = async () => {
      pollRef.current = null;
      let status: string | undefined;
      let errorMessage: string | undefined;
      try {
        const r = await api.get(`/api/v1/network-devices/${deviceId}/command/${commandId}`);
        status = r.data?.status;
        errorMessage = r.data?.error_message;
      } catch {
        /* Transient error polling status — keep trying until the timeout. */
      }
      if (status === "completed") {
        await load();
        setNotice(`Change applied to ${label}.`);
        setBusy(false);
        return;
      }
      if (status === "failed") {
        setNotice(
          `Change failed for ${label}: ${errorMessage || "the switch rejected the command."}`,
        );
        setBusy(false);
        return;
      }
      if (Date.now() - startedAt > TIMEOUT_MS) {
        // The collector may be offline or slow. Refresh once so the user sees
        // whatever state landed, and stop the spinner rather than hang forever.
        await load();
        setNotice(
          `Change for ${label} is taking longer than expected — the on-site collector may be ` +
            `offline. Use Refresh to check whether it applied.`,
        );
        setBusy(false);
        return;
      }
      pollRef.current = window.setTimeout(poll, POLL_MS);
    };
    // Give the collector a moment to pick up the queued command before first poll.
    pollRef.current = window.setTimeout(poll, POLL_MS);
  };

  if (loading)
    return <div className="text-sm text-gray-400 py-6 text-center">Loading port table…</div>;
  if (err)
    return (
      <div className="text-xs text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded p-3 my-4">
        {err}
      </div>
    );
  if (!detail || detail.ports.length === 0)
    return (
      <div className="text-xs text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-700 rounded p-3 my-4">
        No interface data yet. The on-site collector agent gathers the device's interface table (and
        VLANs, for switches) over SNMP on its next poll cycle (~5 min). Confirm an agent is online
        on this device's LAN.
      </div>
    );

  const sel = selected != null ? detail.ports.find((p) => p.if_index === selected) : null;
  // Port writes use Cisco switchport CLI, so only offer them on switches; the
  // faceplate is read-only (view interfaces/status/VLAN) for routers/FWs/APs.
  const canWrite = canManage && device.has_ssh_credentials && device.kind === "switch";

  // Order ports by their printed number (gi1, Gi1/0/24 → 1, 24) so the
  // faceplate lays out like the real switch; fall back to ifIndex.
  const portNumOf = (p: SwitchPort) => {
    const m = (p.name || "").match(/(\d+)\s*$/);
    return m ? parseInt(m[1], 10) : p.if_index;
  };

  // WHICH STACK UNIT a port belongs to. "Gi1/0/24" / "Te2/0/1" → 1, 2.
  // Null for a standalone switch ("gi24"), which then forms a single group
  // and renders exactly as it did before.
  const unitOf = (p: SwitchPort): number | null => {
    const m = (p.name || "").match(/^[A-Za-z]+(\d+)\/\d+\/\d+/);
    return m ? parseInt(m[1], 10) : null;
  };

  // One entry per physical switch, in unit order. A stack of five renders
  // five chassis; anything else renders one, as before.
  //
  // This also fixes a quieter bug: portNumOf() reads the LAST number only,
  // so Gi1/0/24 and Gi5/0/24 sorted equal and the units were interleaved
  // in a single strip - the faceplate was not merely long, it was showing
  // ports in an order that does not exist on any of the hardware.
  const portGroups = (() => {
    const by = new Map<number | null, SwitchPort[]>();
    for (const p of detail.ports) {
      const u = unitOf(p);
      if (!by.has(u)) by.set(u, []);
      by.get(u)!.push(p);
    }
    for (const list of by.values()) list.sort((a, b) => portNumOf(a) - portNumOf(b));
    return [...by.entries()]
      .sort((a, b) => (a[0] ?? 0) - (b[0] ?? 0))
      .map(([unit, ports]) => ({ unit, ports }));
  })();

  // VLAN colours by POSITION, not by `vid % palette.length`. With 22 VLANs
  // an 8-colour modulo guarantees collisions and picks them arbitrarily -
  // VLAN 1, 101 and 140 all came out the same green, which makes the
  // "port colour = access VLAN" legend meaningless. Position-indexing over
  // a 16-colour palette makes the first sixteen distinct.
  const vlanSlot = (() => {
    const ids = [...new Set(detail.ports.map((p) => p.pvid).filter((v): v is number => v != null))]
      .concat(detail.vlans.map((v) => v.vlan_id))
      .filter((v, i, a) => a.indexOf(v) === i)
      .sort((a, b) => a - b);
    const m = new Map<number, number>();
    ids.forEach((id, i) => m.set(id, i));
    return m;
  })();
  const vlanFill = (vid?: number | null) =>
    vid == null ? "bg-gray-500" : VLAN_BAR[(vlanSlot.get(vid) ?? vid) % VLAN_BAR.length];
  const vlanChip = (vid?: number | null) =>
    vid == null
      ? "bg-gray-100 text-gray-600 border-gray-300 dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600"
      : VLAN_PALETTE[(vlanSlot.get(vid) ?? vid) % VLAN_PALETTE.length];

  const applyPort = () => {
    if (!sel) return;
    const tagged = editMode === "access" ? [] : Array.from(editTagged).sort((a, b) => a - b);
    const vlanId = editVlan === "" ? null : editVlan;
    const summary =
      `Apply to ${sel.name || sel.if_index}:\n` +
      `• Mode: ${editMode}\n` +
      `• ${editMode === "access" ? "Access" : "Native"} VLAN: ${vlanId ?? "none"}` +
      (editMode !== "access"
        ? `\n• Tagged VLANs: ${tagged.length ? tagged.join(", ") : "none"}`
        : "");
    sendChange(
      sel,
      { action: "set_port", mode: editMode, vlan_id: vlanId, tagged_vlans: tagged },
      summary,
    );
  };

  return (
    <div className="border-t border-gray-100 dark:border-gray-700 mt-2 pt-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-100">
          Ports <span className="font-normal text-gray-400">({detail.ports.length})</span>
        </h3>
        <span className="text-[11px] text-gray-400">
          Port color = access VLAN · green LED = link up (dim = down) · T = trunk · click to manage.
        </span>
      </div>

      {/* Switch faceplate — a chassis with ports in the real 2-row interleaved
          layout (odd on top, even on bottom). grid-flow-col + 2 rows fills each
          column top→bottom, so port 1 sits above port 2, etc. */}
      <div className="mb-4 space-y-2">
        {portGroups.map((g) => (
        <div key={g.unit ?? "single"} className="overflow-x-auto">
        <div className="inline-flex items-stretch gap-3 rounded-xl bg-gradient-to-b from-slate-700 to-slate-900 p-3 shadow-inner">
          <div className="flex flex-col justify-center px-2 border-r border-slate-600 mr-1">
            <div className="text-[9px] uppercase tracking-wider text-slate-400">
              {g.unit != null ? `Unit ${g.unit}` : device.kind}
            </div>
            <div className="text-xs font-semibold text-slate-100 max-w-[8rem] truncate">
              {device.name}
            </div>
            <div className="text-[9px] text-slate-400 font-mono">{g.ports.length} ports</div>
          </div>
          <div className="grid grid-rows-2 grid-flow-col gap-1 auto-cols-max">
            {g.ports.map((p) => {
              const isSel = p.if_index === selected;
              const oper = p.oper_up;
              const tagged = p.tagged_vlans && p.tagged_vlans.length > 0;
              const disabled = p.admin_up === false;
              return (
                <button
                  key={p.if_index}
                  onClick={() => setSelected(isSel ? null : p.if_index)}
                  title={`${p.name || p.if_index}\nAccess VLAN ${p.pvid ?? "—"}${
                    tagged ? `\nTagged: ${p.tagged_vlans!.join(", ")}` : ""
                  }\n${oper ? "link up" : "link down"}${
                    oper && p.speed_mbps ? ` · ${p.speed_mbps} Mbps` : ""
                  }${
                    p.in_bps != null || p.out_bps != null
                      ? `\n↓ ${fmtBps(p.in_bps)}  ↑ ${fmtBps(p.out_bps)}`
                      : ""
                  }${disabled ? "\n(admin disabled)" : ""}`}
                  className={`relative w-9 h-8 rounded-sm border border-black/30 flex items-center justify-center text-[9px] font-mono font-semibold text-white transition
                    ${vlanFill(p.pvid)}
                    ${disabled ? "opacity-25 line-through" : oper ? "" : "opacity-40"}
                    ${isSel ? "ring-2 ring-blue-400 ring-offset-1 ring-offset-slate-800" : ""}
                    hover:brightness-110`}
                >
                  {portNumOf(p)}
                  {tagged && (
                    <span className="absolute top-0 left-0 text-[7px] leading-none px-0.5 text-amber-200">
                      T
                    </span>
                  )}
                  {/* link LED — green (lit) when up, dark when down, like real gear */}
                  <span
                    className={`absolute top-0.5 right-0.5 w-1.5 h-1.5 rounded-full ${
                      oper ? "bg-green-300 shadow-[0_0_3px_rgba(134,239,172,0.95)]" : "bg-black/50"
                    }`}
                  />
                </button>
              );
            })}
          </div>
        </div>
        </div>
        ))}
      </div>

      {notice && (
        <div className="text-xs bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 text-blue-800 dark:text-blue-200 rounded p-2 mb-4">
          {notice}
        </div>
      )}

      {sel && (
        <div className="bg-gray-50 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-700 rounded-lg p-3 mb-5 text-xs">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
            <div>
              <div className="text-gray-400 uppercase">Port</div>
              <div className="font-mono font-semibold">{sel.name || sel.if_index}</div>
            </div>
            <div>
              <div className="text-gray-400 uppercase">Link</div>
              <div>{sel.oper_up ? "Up" : "Down"}</div>
            </div>
            <div>
              <div className="text-gray-400 uppercase">Admin</div>
              <div>{sel.admin_up === false ? "Disabled" : "Enabled"}</div>
            </div>
            <div>
              <div className="text-gray-400 uppercase">Speed</div>
              <div>{sel.oper_up && sel.speed_mbps ? `${sel.speed_mbps} Mbps` : "—"}</div>
            </div>
            <div>
              <div className="text-gray-400 uppercase">Throughput</div>
              <div className="font-mono">
                <span className="text-emerald-600 dark:text-emerald-400" title="Inbound (rx)">
                  ↓ {fmtBps(sel.in_bps)}
                </span>{" "}
                <span className="text-blue-600 dark:text-blue-400" title="Outbound (tx)">
                  ↑ {fmtBps(sel.out_bps)}
                </span>
              </div>
            </div>
            <div>
              <div className="text-gray-400 uppercase">Mode</div>
              <div>
                {sel.tagged_vlans && sel.tagged_vlans.length > 0 ? "Trunk / general" : "Access"}
                <span className="text-gray-400"> (from VLANs)</span>
              </div>
            </div>
            <div className="col-span-2">
              <div className="text-gray-400 uppercase">Access VLAN (untagged)</div>
              <div>
                {sel.pvid != null ? `${sel.pvid} — ${vlanName.get(sel.pvid) || "unnamed"}` : "—"}
              </div>
            </div>
            <div className="col-span-2">
              <div className="text-gray-400 uppercase">Tagged VLANs</div>
              <div>
                {sel.tagged_vlans && sel.tagged_vlans.length > 0
                  ? sel.tagged_vlans.join(", ")
                  : "—"}
              </div>
            </div>
          </div>

          {canWrite ? (
            <div className="border-t border-gray-200 dark:border-gray-700 pt-3 flex flex-wrap items-end gap-3">
              <button
                disabled={busy}
                onClick={() =>
                  sendChange(
                    sel,
                    { action: sel.admin_up === false ? "enable" : "disable" },
                    `${sel.admin_up === false ? "Enable" : "Disable"} port ${sel.name || sel.if_index}?`,
                  )
                }
                className={`text-xs px-3 py-1.5 rounded-lg text-white disabled:opacity-50 ${
                  sel.admin_up === false
                    ? "bg-green-600 hover:bg-green-700"
                    : "bg-red-600 hover:bg-red-700"
                }`}
              >
                {sel.admin_up === false ? "Enable port" : "Disable port"}
              </button>

              {/* Staged port editor: set mode + VLAN(s), then Apply together. */}
              <div className="flex flex-col gap-2 grow">
                <div className="flex flex-wrap items-end gap-2">
                  <div>
                    <label className="text-[10px] text-gray-400 uppercase block mb-0.5">Mode</label>
                    <select
                      value={editMode}
                      disabled={busy}
                      onChange={(e) => setEditMode(e.target.value)}
                      className="border dark:bg-gray-900 dark:border-gray-600 dark:text-gray-100 rounded-lg px-2 py-1.5 text-xs"
                    >
                      {["access", "trunk", "general"].map((m) => (
                        <option key={m} value={m}>
                          {m}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-[10px] text-gray-400 uppercase block mb-0.5">
                      {editMode === "access" ? "Access VLAN" : "Native VLAN (untagged)"}
                    </label>
                    <select
                      value={editVlan}
                      disabled={busy}
                      onChange={(e) => setEditVlan(e.target.value ? Number(e.target.value) : "")}
                      className="border dark:bg-gray-900 dark:border-gray-600 dark:text-gray-100 rounded-lg px-2 py-1.5 text-xs"
                    >
                      <option value="">— none —</option>
                      {detail.vlans.map((v) => (
                        <option key={v.vlan_id} value={v.vlan_id}>
                          {v.vlan_id} {v.name || ""}
                        </option>
                      ))}
                    </select>
                  </div>
                  <button
                    disabled={busy || (editMode === "access" && editVlan === "")}
                    onClick={applyPort}
                    className="text-xs px-4 py-1.5 rounded-lg bg-brand text-white hover:opacity-90 disabled:opacity-50"
                  >
                    {busy ? "Applying…" : "Apply"}
                  </button>
                </div>
                {editMode !== "access" && (
                  <div>
                    <label className="text-[10px] text-gray-400 uppercase block mb-1">
                      Tagged VLANs {editMode === "trunk" ? "(trunk allowed)" : "(general tagged)"}
                    </label>
                    <div className="flex flex-wrap gap-1.5">
                      {detail.vlans.length === 0 && (
                        <span className="text-[11px] text-gray-400">
                          No VLANs known on this switch yet.
                        </span>
                      )}
                      {detail.vlans.map((v) => {
                        const on = editTagged.has(v.vlan_id);
                        const isNative = editVlan !== "" && v.vlan_id === editVlan;
                        return (
                          <label
                            key={v.vlan_id}
                            className={`text-xs px-2 py-1 rounded border cursor-pointer select-none ${
                              on
                                ? "bg-brand text-white border-brand"
                                : "bg-white dark:bg-gray-900 border-gray-300 text-gray-700 dark:text-gray-200"
                            } ${isNative ? "opacity-50" : ""}`}
                            title={isNative ? "This is the native (untagged) VLAN" : ""}
                          >
                            <input
                              type="checkbox"
                              className="mr-1 align-middle"
                              checked={on}
                              disabled={busy}
                              onChange={() =>
                                setEditTagged((prev) => {
                                  const n = new Set(prev);
                                  if (on) n.delete(v.vlan_id);
                                  else n.add(v.vlan_id);
                                  return n;
                                })
                              }
                            />
                            {v.vlan_id} {v.name || ""}
                          </label>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : canManage ? (
            <div className="border-t border-gray-200 dark:border-gray-700 pt-3 text-[11px] text-amber-700 dark:text-amber-300">
              To change ports, add SSH credentials to this switch (Edit → SSH username/password).
              Enable/disable, VLAN, and port-mode changes are pushed over SSH via the on-site
              collector.
            </div>
          ) : null}
        </div>
      )}

      <div className="flex items-center justify-end mb-2">
        <button onClick={load} className="text-[11px] text-blue-600 dark:text-blue-400 hover:underline">
          ↻ Refresh
        </button>
      </div>
      {(device.kind === "switch" || detail.vlans.length > 0) && (
        <>
          <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-100 mb-2">
            VLANs <span className="font-normal text-gray-400">({detail.vlans.length})</span>
          </h3>
          {detail.vlans.length === 0 ? (
            <p className="text-xs text-gray-400">No VLAN table reported.</p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {detail.vlans.map((v) => {
                const count = detail.ports.filter((p) => p.pvid === v.vlan_id).length;
                return (
                  <div
                    key={v.vlan_id}
                    className={`inline-flex items-center text-xs px-2.5 py-1 rounded-full border ${vlanChip(v.vlan_id)}`}
                  >
                    <span
                      className={`inline-block w-2 h-2 rounded-full mr-1.5 ${vlanFill(v.vlan_id)}`}
                    />
                    <span className="font-semibold">{v.vlan_id}</span>{" "}
                    {v.name || `VLAN ${v.vlan_id}`}
                    <span className="ml-1 opacity-70 dark:opacity-90">
                      · {count} access port{count === 1 ? "" : "s"}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// "GigabitEthernet1/0/24" → "24"; "gi24" → "24"; falls back to ifIndex.
